<template>
  <div class="lunar-module">
    <div class="lunar-env-banner">
      <div>🌕 月球基地 · 番茄农场</div>
      <div class="env-details">
        🌙 低重力 &nbsp;|&nbsp; 真空 &nbsp;|&nbsp; 无液态水 &nbsp;|&nbsp; 极度温差 (−173℃~127℃) &nbsp;|&nbsp; 强宇宙辐射
      </div>
      <div class="think-tip">🤔 思考番茄需要什么环境？开启合适设备！</div>
    </div>

    <div class="dashboard">
      <div class="tomato-card">
        <h3>🍅 月面番茄生长实况</h3>
        <div class="tomato-visual">
          <div class="state-emoji">{{ stateEmoji }}</div>
          <div class="state-title">{{ stateTitle }}</div>
          <div class="state-desc">{{ stateDesc }}</div>
          <div class="mutant-note" v-show="showSpecialNote">{{ specialNote }}</div>
        </div>
      </div>

      <div class="env-panel">
        <h3>🛠️ 设备控制 · 开关代表有/无</h3>
        <div class="env-indicator-grid">
          <div class="env-item">
            <div class="env-label">⚡ 电力系统</div>
            <button class="toggle-btn-icon" :class="{ active: powerOn }" @click="togglePower">⚡</button>
            <div class="env-status" :style="{ background: powerOn ? '#4c8c1a' : '#7a5a2e' }">
              {{ powerOn ? '供电 ✅' : '断电 ❌' }}
            </div>
          </div>

          <div class="env-item">
            <div class="env-label">🌬️ 空气设备 (氧气/气压)</div>
            <button class="toggle-btn-icon" :class="{ active: airOn }" @click="toggleAir">🌬️</button>
            <div class="env-status" :style="{ background: airEffective ? '#2f7312' : '#9b6a2c' }">
              {{ airEffective ? '充足 ✅' : (powerOn ? '不足 ❌' : '断电失效⚠️') }}
            </div>
          </div>

          <div class="env-item">
            <div class="env-label">💧 水循环再生器</div>
            <button class="toggle-btn-icon" :class="{ active: waterOn }" @click="toggleWater">💧</button>
            <div class="env-status" :style="{ background: waterEffective ? '#2f7312' : '#9b6a2c' }">
              {{ waterEffective ? '充足 ✅' : (powerOn ? '不足 ❌' : '断电失效⚠️') }}
            </div>
          </div>

          <div class="env-item">
            <div class="env-label">🌡️ 温控调节仪</div>
            <button class="toggle-btn-icon" :class="{ active: tempOn }" @click="toggleTemp">🌡️</button>
            <div class="env-status" :style="{ background: tempEffective ? '#2f7312' : '#9b6a2c' }">
              {{ tempEffective ? '适宜 🌡️✅' : (powerOn ? '不适宜 ❌' : '断电失效⚠️') }}
            </div>
          </div>

          <div class="env-item">
            <div class="env-label">☀️ 光照控制装置</div>
            <button class="toggle-btn-icon" :class="{ active: lightOn }" @click="toggleLight">☀️</button>
            <div class="env-status" :style="{ background: lightEffective ? '#2f7312' : '#9b6a2c' }">
              {{ lightEffective ? '充足 💡✅' : (powerOn ? '不足 ❌' : '断电失效⚠️') }}
            </div>
          </div>

          <div class="env-item">
            <div class="env-label">🪢 防歪支架 (抗低重力倒伏)</div>
            <button class="toggle-btn-icon" :class="{ active: stakeOn }" @click="toggleStake">🪢</button>
            <div class="env-status" :style="{ background: stakeOn ? '#2f7312' : '#9b6a2c' }">
              {{ stakeOn ? '已安装 ✅' : '未安装 ❌' }}
            </div>
          </div>
        </div>

        <button class="toggle-btn-icon reset-btn-icon" @click="resetAll">🔄 重置全部</button>
        <button class="toggle-btn-icon reset-btn-icon" @click="toClose">返回</button>
      </div>
    </div>

    <div class="history-area">
      <div class="history-title">📜 生长记录 (最近2~3次结果)</div>
      <div class="history-list">
        <div v-if="historyStack.length === 0" class="history-card">✨ 暂无记录，操作设备后出现</div>
        <div v-for="(item, idx) in historyStack" :key="idx" class="history-card">
          {{ item.emoji }} {{ item.stateText }} <span style="font-size:0.7rem;">{{ item.time }}</span>
        </div>
      </div>
    </div>

    <div class="info-footer">
      💡 番茄需求: 空气、液态水替代、适宜温度(15~30℃)、充足光照、支架防倒伏，以及稳定电力！<br>
      🧬 特殊变异: 当环境不完美但未致死时可能发生变异 → 20%畸形果+1%超优黄金果+79%劣果
    </div>
  </div>
</template>

<script>
export default {
  name: 'MoonTomato',
  data() {
    return {
      powerOn: false,
      airOn: false,
      waterOn: false,
      tempOn: false,
      lightOn: false,
      stakeOn: false,

      stateEmoji: '🌰🪨',
      stateTitle: '🌱 不发芽',
      stateDesc: '种子休眠中，缺乏生命维持系统。',
      showSpecialNote: false,
      specialNote: '',

      historyStack: [],
      lastState: null,
    }
  },
  computed: {
    airEffective() { return this.powerOn && this.airOn },
    waterEffective() { return this.powerOn && this.waterOn },
    tempEffective() { return this.powerOn && this.tempOn },
    lightEffective() { return this.powerOn && this.lightOn },
  },
  mounted() {
    this.init()
  },
  methods: {
    toClose(){
      this.$emit('toBack')
    },
    init() {
      this.powerOn = false
      this.airOn = false
      this.waterOn = false
      this.tempOn = false
      this.lightOn = false
      this.stakeOn = false
      this.historyStack = []
      this.lastState = null
      this.refreshFull()
    },
    togglePower() { this.powerOn = !this.powerOn; this.refreshFull() },
    toggleAir() { this.airOn = !this.airOn; this.refreshFull() },
    toggleWater() { this.waterOn = !this.waterOn; this.refreshFull() },
    toggleTemp() { this.tempOn = !this.tempOn; this.refreshFull() },
    toggleLight() { this.lightOn = !this.lightOn; this.refreshFull() },
    toggleStake() { this.stakeOn = !this.stakeOn; this.refreshFull() },

    evaluateGrowthState() {
      if (!this.powerOn) return 'DEAD'
      if (!this.airOn || !this.waterOn) return 'DEAD'

      const hasTemp = this.tempOn
      const hasLight = this.lightOn
      const perfect = hasTemp && hasLight && this.stakeOn
      const canSprout = hasTemp || hasLight

      if (!canSprout) return 'SEED'
      if (!perfect) {
        const roll = Math.random()
        if (roll < 0.28) return 'MUTANT'
        return 'SPROUT'
      }
      return 'NORMAL'
    },

    updateTomatoDisplay(state) {
      this.showSpecialNote = false
      this.specialNote = ''

      switch (state) {
        case 'SEED':
          this.stateEmoji = '🌰🪨'
          this.stateTitle = '🌱 不发芽'
          this.stateDesc = '种子处于休眠状态，缺乏温度或光照信号，无法启动生长。请开启温控或光照设备。'
          break
        case 'SPROUT':
          this.stateEmoji = '🌱🍃'
          this.stateTitle = '🌿 发芽但不生长'
          this.stateDesc = '嫩芽冒出，但环境尚未完美：可能缺少温度/光照或防歪支架，导致茎叶矮小、停滞发育。'
          break
        case 'NORMAL':
          this.stateEmoji = '🍅🍅✨'
          this.stateTitle = '🍅 正常生长结果'
          this.stateDesc = '恭喜！空气、水、适宜温度、充足光照、支架稳固，电力保障，番茄繁茂结果，收获喜人。'
          break
        case 'DEAD':
          this.stateEmoji = '🥀🍂🌾'
          this.stateTitle = '💔 枯萎死亡'
          this.stateDesc = '番茄植株完全枯死，因电力中断/缺氧/缺水或缺乏关键生命维持系统。'
          break
        case 'MUTANT':
          this.stateEmoji = '🧬🍅🌀'
          this.stateTitle = '🧬 变异植株 (宇宙辐射诱发)'
          this.stateDesc = '极端环境诱变：约20%果实畸形瘤化，1%突变为“超级黄金番茄”，其余79%品质低劣。'
          this.showSpecialNote = true
          this.specialNote = '⚠️ 变异数据: 20%畸形果 + 1%超优黄金果 + 79%劣果'
          break
      }
    },

    getStateEmojiShort(state) {
      const map = { SEED:'🌰', SPROUT:'🌱', NORMAL:'🍅', DEAD:'🥀', MUTANT:'🧬' }
      return map[state] || '❓'
    },

    addHistoryRecord(state, text) {
      if (this.historyStack[0].stateText === text) return
      const obj = {
        stateText: text,
        emoji: this.getStateEmojiShort(state),
        time: new Date().toLocaleTimeString()
      }
      this.historyStack.unshift(obj)
      if (this.historyStack.length > 3) this.historyStack.pop()
    },

    refreshFull() {
      const state = this.evaluateGrowthState()
      this.updateTomatoDisplay(state)

      const stateMap = {
        SEED: '不发芽',
        SPROUT: '发芽但不生长',
        NORMAL: '正常生长结果',
        DEAD: '枯萎死亡',
        MUTANT: '变异'
      }
      const text = stateMap[state] || '未知'
      if (this.lastState !== state) {
        this.addHistoryRecord(state, text)
        this.lastState = state
      }
    },

    resetAll() {
      this.powerOn = false
      this.airOn = false
      this.waterOn = false
      this.tempOn = false
      this.lightOn = false
      this.stakeOn = false
      this.refreshFull()
    }
  }
}
</script>

<style scoped>
.lunar-module {
  width: 100%;
  background: rgba(242, 255, 190, 0.92);
  border-radius: 24px;
  border: 3px solid #8fbc2c;
  box-shadow: 0 15px 30px rgba(30, 40, 5, 0.2);
  padding: 20px;
  font-family: 'Segoe UI', 'Arial', sans-serif;
  color: #1e3a0c;
}
.lunar-env-banner {
  background: #edffb0e0;
  border-radius: 20px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 8px solid #5f8817;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-weight: bold;
}
.env-details {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.9rem;
  background: #fff1b5;
  padding: 4px 12px;
  border-radius: 20px;
}
.think-tip {
  background: #f9e68c;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.9rem;
}
.dashboard {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}
.tomato-card {
  flex: 1.4;
  min-width: 280px;
  background: #feffdd;
  border-radius: 20px;
  padding: 16px;
  border: 2px solid #9bc13a;
  text-align: center;
}
.env-panel {
  flex: 1;
  background: #f2ffce;
  border-radius: 20px;
  padding: 16px;
  border: 2px solid #a2c93e;
}
.tomato-visual {
  background: #eef5bc;
  border-radius: 16px;
  margin: 16px 0;
  padding: 16px;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #b4dc4a;
}
.state-emoji {
  font-size: 100px;
  line-height: 1;
}
.state-title {
  font-size: 26px;
  font-weight: bold;
  margin: 10px 0 6px;
  color: #2c6e1a;
}
.state-desc {
  background: #ffffe6;
  padding: 10px;
  border-radius: 20px;
  font-size: 15px;
  color: #2b521a;
  border: 1px solid #bfe060;
}
.mutant-note {
  color: #c1661a;
  font-weight: bold;
  background: #fff0bb;
  border-radius: 20px;
  padding: 6px 12px;
  margin-top: 10px;
  font-size: 13px;
}
.env-indicator-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 16px 0;
}
.env-item {
  background: #e2f0b0;
  border-radius: 40px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  font-weight: bold;
  font-size: 16px;
  border: 1px solid #9fc73a;
}
.env-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
}
.env-status {
  color: #f9ffcf;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 15px;
  min-width: 100px;
  text-align: center;
}
.toggle-btn-icon {
  background: #cbe57a;
  border: none;
  padding: 6px 14px;
  border-radius: 40px;
  font-size: 20px;
  cursor: pointer;
  transition: 0.2s;
  border: 1px solid #eaffb2;
  min-width: 60px;
  text-align: center;
}
.toggle-btn-icon.active {
  background: #5f9e2a;
}
.toggle-btn-icon:hover {
  transform: scale(0.96);
}
.reset-btn-icon {
  background: #e3bc6e;
  font-size: 16px;
  padding: 10px;
  width: 100%;
  margin-top: 10px;
}
.history-area {
  margin-top: 20px;
  background: #eaf7bb;
  border-radius: 16px;
  padding: 12px 16px;
  border-top: 3px solid #9ac432;
}
.history-title {
  font-size: 16px;
  font-weight: bold;
}
.history-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.history-card {
  background: #ffffdd;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 15px;
  font-weight: bold;
  border-left: 6px solid #8fc23a;
}
.info-footer {
  margin-top: 12px;
  font-size: 13px;
  text-align: center;
  background: #eef6c9;
  border-radius: 12px;
  padding: 8px;
}
</style>